import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import api from '../../services/api';
import { TestRunState } from '../../types/attacks';

export interface TestRunRequestParams {
  attack_type_id: string;
  attack_type_name: string;
  configuration: Record<string, any>;
  max_rps?: number | null;
  max_bandwidth_bps?: number | null;
  max_pps?: number | null;
}

interface TestRunDialogProps {
  open: boolean;
  request: TestRunRequestParams | null;
  onClose: () => void;
}

type Phase = 'starting' | 'provisioning' | 'running' | 'completed' | 'error';

const POLL_INTERVAL_MS = 3000;

/**
 * Orchestrates a "Test Run" (CAO-156): start (create one ephemeral bot running the
 * in-progress scenario-creation configuration exactly once) → poll until the bot
 * comes up and the run completes → show the captured attack output. Terminates the
 * test-run bot when the dialog is closed.
 */
const TestRunDialog: React.FC<TestRunDialogProps> = ({ open, request, onClose }) => {
  const [phase, setPhase] = useState<Phase>('starting');
  const [runState, setRunState] = useState<TestRunState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);

  const pollTimer = useRef<number | null>(null);
  const startedRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const applyState = useCallback((state: TestRunState) => {
    setRunState(state);
    if (state.status === 'error' || state.test_run_status === 'failed') {
      setPhase('error');
      setErrorMsg(state.error || 'The test run failed.');
    } else if (state.test_run_status === 'completed') {
      setPhase('completed');
    } else if (state.status === 'running') {
      setPhase('running');
    } else {
      setPhase('provisioning');
    }
  }, []);

  // Start the test run once when the dialog opens.
  useEffect(() => {
    if (!open || !request || startedRef.current) return;
    startedRef.current = true;
    setPhase('starting');
    setErrorMsg(null);
    setRunState(null);
    (async () => {
      try {
        const state = await api.startTestRun({
          attack_type_id: request.attack_type_id,
          configuration: request.configuration,
          max_rps: request.max_rps,
          max_bandwidth_bps: request.max_bandwidth_bps,
          max_pps: request.max_pps,
        });
        applyState(state);
      } catch (err: any) {
        setPhase('error');
        setErrorMsg(err?.response?.data?.detail || err?.message || 'Failed to start test run.');
      }
    })();
  }, [open, request, applyState]);

  // Poll until the run reaches a terminal state (completed/failed).
  useEffect(() => {
    if (!open) return undefined;
    if (phase !== 'provisioning' && phase !== 'starting' && phase !== 'running') return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const state = await api.getTestRun();
        if (cancelled) return;
        if (state.active === false) {
          setPhase('error');
          setErrorMsg('The test-run bot is no longer active.');
          return;
        }
        applyState(state);
        if (!cancelled && state.test_run_status !== 'completed' && state.test_run_status !== 'failed' && state.status !== 'error') {
          pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (!cancelled) pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [open, phase, applyState, clearPoll]);

  // Terminate the test-run bot and reset when the dialog closes.
  const handleClose = useCallback(async () => {
    clearPoll();
    startedRef.current = false;
    setPhase('starting');
    setRunState(null);
    setErrorMsg(null);
    setStopping(false);
    onClose();
    try {
      await api.stopTestRun();
    } catch {
      /* best-effort teardown; supervisor safety-net will reap orphans */
    }
  }, [onClose, clearPoll]);

  const handleStopClick = useCallback(async () => {
    setStopping(true);
    await handleClose();
  }, [handleClose]);

  const statusLine = (() => {
    if (phase === 'completed') return 'completed';
    if (phase === 'error') return 'error';
    if (phase === 'running') return 'running attack…';
    const status = runState?.status;
    return status ? `provisioning · ${status}` : 'provisioning…';
  })();
  const statusColor =
    phase === 'error' ? 'error.main' : phase === 'completed' ? 'success.main' : 'text.secondary';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <PlayCircleOutlineIcon fontSize="small" />
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Test Run · {request?.attack_type_name || 'attack'}
        </Typography>
        <Typography component="span" variant="caption" sx={{ color: statusColor, ml: 1 }}>
          {statusLine}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Close (stops the test-run bot)">
          <IconButton
            aria-label="stop test run"
            onClick={handleStopClick}
            disabled={stopping}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {phase === 'error' ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg || 'Test run failed.'}
            </Alert>
            {runState?.output && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  maxHeight: '40vh',
                  overflow: 'auto',
                  fontFamily: '"JetBrains Mono", "Space Mono", monospace',
                  fontSize: '0.8rem',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.04)',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {runState.output}
              </Box>
            )}
            <Button variant="outlined" onClick={handleStopClick} startIcon={<CloseIcon />} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        ) : phase === 'completed' ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Test run completed. Captured output below.
            </Alert>
            <Box
              component="pre"
              data-testid="test-run-output"
              sx={{
                m: 0,
                p: 2,
                maxHeight: '50vh',
                overflow: 'auto',
                fontFamily: '"JetBrains Mono", "Space Mono", monospace',
                fontSize: '0.8rem',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.04)',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {runState?.output || '(no output captured)'}
            </Box>
            <Button variant="outlined" onClick={handleStopClick} startIcon={<CloseIcon />} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              minHeight: '30vh',
            }}
          >
            <CircularProgress size={56} />
            <Typography
              variant="body1"
              sx={{ fontFamily: '"Space Mono", monospace', color: 'text.secondary', textAlign: 'center' }}
            >
              {phase === 'starting'
                ? 'Starting test run — creating one bot…'
                : phase === 'running'
                ? 'Running the attack once and capturing its output…'
                : 'Provisioning the test-run bot…'}
            </Typography>
            {runState?.status && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                status: {runState.status}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TestRunDialog;
